import React, { useState, useEffect } from 'react';
import {
  BarChart3, TrendingUp, TrendingDown, Wallet, FileText,
  ArrowUpRight, ArrowDownRight, CreditCard, AlertCircle,
  ChevronLeft, ChevronRight, Loader2, PiggyBank,
  Receipt
} from 'lucide-react';

import api from '../../services/api';
import { Toast } from '../ui/Toast';
import { useLoader } from '../../contexts/LoaderContext';
import { useParamsCompta } from '../../contexts/ParametresComptaContext';

// ============================================================================
// REPORTING MODULE — Dashboard financier consolidé
// ============================================================================

const MOIS_LABELS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const ReportingModule = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  const [selectedMois, setSelectedMois] = useState(new Date().getMonth() + 1);
  const [selectedAnnee, setSelectedAnnee] = useState(new Date().getFullYear());

  const { showLoader, hideLoader } = useLoader();
  const { formatMontant, formatMontantCourt, symbole } = useParamsCompta();
  const showToast = (m, t = 'success') => setToast({ show: true, message: m, type: t });

  useEffect(() => {
    if (toast.show) { const t = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000); return () => clearTimeout(t); }
  }, [toast.show]);

  useEffect(() => { loadData(); }, [selectedMois, selectedAnnee]);

  const loadData = async () => {
    showLoader(); setIsLoading(true);
    try {
      const response = await api.reporting.getGlobal({ mois: selectedMois, annee: selectedAnnee });
      if (response.success) setData(response.data);
      else showToast(response.message || 'Erreur', 'error');
    } catch (error) {
      console.error('Erreur:', error);
      showToast('Erreur lors du chargement', 'error');
    } finally { setIsLoading(false); hideLoader(); }
  };

  const goMoisPrecedent = () => {
    if (selectedMois === 1) { setSelectedMois(12); setSelectedAnnee(selectedAnnee - 1); }
    else setSelectedMois(selectedMois - 1);
  };
  const goMoisSuivant = () => {
    if (selectedMois === 12) { setSelectedMois(1); setSelectedAnnee(selectedAnnee + 1); }
    else setSelectedMois(selectedMois + 1);
  };


  const getBarWidth = (value, max) => max > 0 ? Math.min((value / max) * 100, 100) : 0;

  

  if (!data) return null;

  const maxEvolution = Math.max(...(data.evolution || []).map(e => Math.max(parseFloat(e.revenus), parseFloat(e.depenses))), 1);
  const maxDepense = data.top_depenses?.length > 0 ? parseFloat(data.top_depenses[0].total) : 1;

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#fdfcf5' }}>

      {/* Sélecteur période */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <BarChart3 size={22} style={{ color: '#448696' }} />
          <h2 className="text-lg font-bold" style={{ color: '#1a1a2e' }}>Tableau de bord financier</h2>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-xl px-2 py-1 border border-gray-200" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <button onClick={goMoisPrecedent} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-all">
            <ChevronLeft size={16} className="text-gray-600" />
          </button>
          <div className="px-3 py-1.5 text-sm font-bold" style={{ color: '#1a1a2e', minWidth: '160px', textAlign: 'center' }}>
            {MOIS_LABELS[selectedMois - 1]} {selectedAnnee}
          </div>
          <button onClick={goMoisSuivant} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-all">
            <ChevronRight size={16} className="text-gray-600" />
          </button>
        </div>
      </div>

      {/* KPIs principaux */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #448696', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#f0f9fb' }}>
              <Wallet size={20} style={{ color: '#448696' }} />
            </div>
          </div>
          <div className="text-2xl font-bold tracking-tight" style={{ color: '#1a1a2e' }}>{formatMontant(data.solde_total)}</div>
          <div className="text-xs font-semibold mt-1 text-gray-500">Solde global trésorerie</div>
        </div>

        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #10b981', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-green-50">
              <ArrowDownRight size={20} className="text-green-600" />
            </div>
            {data.mois.nb_mouvements > 0 && (
              <div className="text-xs font-bold px-2 py-1 rounded-lg bg-green-50 text-green-700">{data.mois.nb_mouvements} mvts</div>
            )}
          </div>
          <div className="text-2xl font-bold tracking-tight text-green-600">{formatMontant(data.mois.revenus)}</div>
          <div className="text-xs font-semibold mt-1 text-gray-500">Revenus du mois</div>
        </div>

        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #ef4444', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-red-50">
              <ArrowUpRight size={20} className="text-red-600" />
            </div>
          </div>
          <div className="text-2xl font-bold tracking-tight text-red-600">{formatMontant(data.mois.depenses)}</div>
          <div className="text-xs font-semibold mt-1 text-gray-500">Dépenses du mois</div>
        </div>

        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: `3px solid ${data.mois.benefice >= 0 ? '#10b981' : '#ef4444'}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: data.mois.benefice >= 0 ? '#d4f4dd' : '#fee2e2' }}>
              <TrendingUp size={20} style={{ color: data.mois.benefice >= 0 ? '#10b981' : '#ef4444' }} />
            </div>
          </div>
          <div className="text-2xl font-bold tracking-tight" style={{ color: data.mois.benefice >= 0 ? '#10b981' : '#ef4444' }}>
            {data.mois.benefice >= 0 ? '+' : ''}{formatMontant(data.mois.benefice)}
          </div>
          <div className="text-xs font-semibold mt-1 text-gray-500">Résultat du mois</div>
        </div>
      </div>

      {/* Facturation */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #6366f1', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><FileText size={16} className="text-indigo-600" /><span className="text-xs font-semibold text-gray-500">CA facturé</span></div>
          <div className="text-xl font-bold text-indigo-600">{formatMontant(data.facturation.ca_facture)}</div>
          <div className="text-xs text-gray-400 mt-1">{data.facturation.nb_factures} facture{data.facturation.nb_factures > 1 ? 's' : ''} • {data.facturation.nb_devis} devis</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #10b981', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><CreditCard size={16} className="text-green-600" /><span className="text-xs font-semibold text-gray-500">CA encaissé</span></div>
          <div className="text-xl font-bold text-green-600">{formatMontant(data.facturation.ca_encaisse)}</div>
          <div className="text-xs text-gray-400 mt-1">Factures payées</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #f59e0b', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><AlertCircle size={16} className="text-amber-600" /><span className="text-xs font-semibold text-gray-500">Impayés</span></div>
          <div className="text-xl font-bold text-amber-600">{formatMontant(data.facturation.impayes)}</div>
          <div className="text-xs text-gray-400 mt-1">Reste à encaisser</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Évolution 6 mois */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <h3 className="text-sm font-bold text-gray-800 mb-5 flex items-center gap-2">
            <TrendingUp size={16} style={{ color: '#448696' }} /> Évolution sur 6 mois
          </h3>
          {data.evolution && data.evolution.length > 0 ? (
            <div className="space-y-4">
              {data.evolution.map((e, i) => {
                const revenus = parseFloat(e.revenus);
                const depenses = parseFloat(e.depenses);
                const [annee, mois] = e.mois_annee.split('-');
                const label = `${MOIS_LABELS[parseInt(mois) - 1]?.substring(0, 3)} ${annee}`;
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-semibold text-gray-500 w-16">{label}</span>
                      <div className="flex items-center gap-4 text-xs font-bold">
                        <span className="text-green-600">+{formatMontantCourt(revenus, false)}</span>
                        <span className="text-red-600">-{formatMontantCourt(depenses, false)}</span>
                      </div>
                    </div>
                    <div className="flex gap-1 h-5">
                      <div className="rounded-l-md transition-all duration-500" style={{ width: `${getBarWidth(revenus, maxEvolution)}%`, backgroundColor: '#10b981', minWidth: revenus > 0 ? '4px' : '0' }} />
                      <div className="rounded-r-md transition-all duration-500" style={{ width: `${getBarWidth(depenses, maxEvolution)}%`, backgroundColor: '#ef4444', minWidth: depenses > 0 ? '4px' : '0' }} />
                    </div>
                  </div>
                );
              })}
              <div className="flex items-center gap-4 pt-3 border-t border-gray-100 text-xs font-semibold text-gray-400">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-green-500" /> Revenus</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-500" /> Dépenses</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400 text-sm">Pas de données sur cette période</div>
          )}
        </div>

        {/* Top dépenses */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <h3 className="text-sm font-bold text-gray-800 mb-5 flex items-center gap-2">
            <Receipt size={16} style={{ color: '#448696' }} /> Top dépenses du mois
          </h3>
          {data.top_depenses && data.top_depenses.length > 0 ? (
            <div className="space-y-4">
              {data.top_depenses.map((d, i) => {
                const total = parseFloat(d.total);
                const colors = ['#ef4444', '#f97316', '#f59e0b', '#6366f1', '#8b5cf6'];
                const color = colors[i] || '#9ca3af';
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-semibold text-gray-700">{d.categorie}</span>
                      <span className="text-sm font-bold" style={{ color }}>{formatMontant(total)}</span>
                    </div>
                    <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${getBarWidth(total, maxDepense)}%`, backgroundColor: color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400 text-sm">Aucune dépense catégorisée</div>
          )}
        </div>
      </div>

      {/* Soldes par caisse */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <h3 className="text-sm font-bold text-gray-800 mb-5 flex items-center gap-2">
          <PiggyBank size={16} style={{ color: '#448696' }} /> Répartition par caisse
        </h3>
        {data.caisses && data.caisses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {data.caisses.map((c) => {
              const solde = parseFloat(c.solde_actuel || 0);
              const pct = data.solde_total > 0 ? (solde / data.solde_total * 100).toFixed(1) : 0;
              return (
                <div key={c.id} className="p-4 rounded-xl border transition-all hover:shadow-md" style={{ borderColor: c.couleur + '30' }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c.couleur }} />
                    <span className="text-sm font-bold text-gray-800">{c.nom}</span>
                  </div>
                  <div className="text-xl font-bold mb-1" style={{ color: c.couleur }}>{formatMontant(solde)}</div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-1">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: c.couleur }} />
                  </div>
                  <div className="text-xs font-semibold text-gray-400">{pct}% du total</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400 text-sm">Aucune caisse active</div>
        )}
      </div>

      {toast.show && <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ show: false, message: '', type: '' })} />}
    </div>
  );
};

export default ReportingModule;